import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";  
import { useGlobalContext } from "../GlobalContext";

export default function useNotification() {
  const { baseurl, token, setIsUnauthorised, AdminId } = useGlobalContext();

  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [sending, setSending] = useState(false);

  const sendNotification = async (form, resetForm) => {
    setSending(true);
    try {
      const response = await axios.post(
        `${baseurl}/api/User/v1.0/sendNotification`,
        form,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (response.data?.responseCode === "00") {
        toast.success("Notification sent successfully!");
        resetForm();
      } else if (response.data?.responseCode === "98") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          response.data?.responseMessage || "Failed to send notification"
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Error sending notification");
    } finally {
      setSending(false);
    }
  };

  // ================= BANNER APIs =================

  const fetchBanners = async (setBanners, setLoading) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/User/v1.0/getAllBanners`,
        { userid: "", adminId: AdminId },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        setBanners(res.data.response || []);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage);
      }
    } catch {
      toast.error("Error fetching banners");
    } finally {
      setLoading(false);
    }
  };

  const uploadBannerImage = async (file) => {
    try {
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 600,
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(file, options);

      const reader = new FileReader();

      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () =>
          resolve(
            reader.result.replace(
              /^data:image\/(jpeg|png|jpg|webp);base64,/,
              ""
            )
          );
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });

      const payload = {
        userid: "banner_user",
        item_name: `banner_${Date.now()}`,
        image_data: base64,
        type: "banner",
        adminId: AdminId,
      };

      const res = await axios.post(
        "https://bumppy.in/packky/api/v1/uploadKycImage.php",
        payload,
        {
          headers: {
            Authorization: `Basic ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.status === "0" || res.data.status === 0) {
        return res.data.image_url || "";
      } else {
        toast.error(res.data.msg || "Upload failed");
        return "";
      }
    } catch (err) {
      console.error(err);
      toast.error("Image upload failed");
      return "";
    }
  };

  const deleteBanner = async (bannerid, onSuccess, setDeletingId) => {
    if (!bannerid) return;

    setDeletingId(bannerid);
    try {
      const res = await axios.post(
        `${baseurl}/api/User/v1.0/deleteBanners`,
        { bannerid, adminId: AdminId },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success("Banner deleted");
        await onSuccess();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error deleting banner");
    } finally {
      setDeletingId(null);
    }
  };

  const insertBanner = async (form, onSuccess, setLoading) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/User/v1.0/insertBanners`,
        form,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success("Banner created successfully!");
        await onSuccess();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Error creating banner!");
      }
    } catch {
      toast.error("Error creating banner!");
    } finally {
      setLoading(false);
    }
  };

  return {
    sendNotification,
    loadingDrivers,
    sending,

    // Banner exports
    fetchBanners,
    uploadBannerImage,
    deleteBanner,
    insertBanner,
  };
}
